/**
 * Reports management page.
 * Tabbed list with {@link CollapseReplaceWrapper} for create/edit forms.
 */

import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate, useNavigationType } from 'react-router-dom';
import { CollapseReplaceWrapper } from '@/components/common/CollapseReplaceWrapper';
import { ReportsDataTable } from '@/components/reports/remote/ReportsDataTable';
import { EditReportView } from '@/components/reports/remote/EditReportView';
import { CreateReportView } from '@/components/reports/remote/CreateReportView';
import { SelectReportTypeModal } from '@/components/reports/remote/SelectReportTypeModal';
import { ManagementPage } from '@/components/common/ManagementPage';
import { usePageActions } from '@/hooks/usePageActions';
import { useInterfaceElements } from '@/hooks/useInterfaceElements';
import { useDetailsView } from '@/hooks/useDetailsView';
import { useViewMode } from '@/hooks/useViewMode';
import { reportKeys } from '@/hooks/useReportsRemote';
import { Button } from '@/components/forms/Button';
import { LayoutGrid } from 'lucide-react';
import type { ReportTab } from '@/types/remote/report.types';
import {
  REMOTE_REPORT_TAB_LABELS as TAB_LABELS,
  REMOTE_REPORT_TAB_SESSION_TYPES as TAB_SESSION_TYPES,
  REMOTE_REPORT_TAB_COLORS as TAB_COLORS,
  SESSION_REMOTE_TYPE_CONFIG as SESSION_TYPE_CONFIG,
} from '@/components/reports/report-configs';
import {
  sessionTypeToReportTab,
  toReportTab,
} from '@/components/reports/remote/report-table-utils';

/** Which form is mounted in the collapse wrapper. */
type ActiveForm = 'edit' | 'create' | null;

/** Tab ids in display order (RBAC subset is sorted back into this sequence). */
const TAB_ORDER: ReportTab[] = [
  'tutoring',
  'information-meeting',
  'welcome-meeting',
  'field-event',
];

/** Remote reports list with type picker and create/edit forms replacing the table. */
export function ReportsManagementPage() {
  const {
    canCreate,
    canEditRowTutoring,
    canEditRowInformation,
    canEditRowWelcome,
    canEditRowFieldEvent,
    canValidateTutoring,
    canValidateInformation,
    canValidateWelcome,
    canValidateFieldEvent,
    canExportExcel,
    creatableTypes: creatableTypesRaw,
  } = usePageActions('reports');
  const { elements, isLoading: elementsLoading } = useInterfaceElements('reports');
  const queryClient = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();

  /** RBAC-allowed tabs; `undefined` while elements load so the table does not query with the wrong types. */
  const allowedTabIds = React.useMemo((): ReportTab[] | undefined => {
    if (elementsLoading) return undefined;
    const tabElements = elements.filter((el) => el.elementType === 'tab');
    const otherTabs: ReportTab[] =
      tabElements.length === 0
        ? TAB_ORDER
        : tabElements
            .map((el) => toReportTab(el.elementId))
            .filter((id): id is ReportTab => id !== null && id !== 'all');

    const sorted = [...otherTabs].sort((a, b) => TAB_ORDER.indexOf(a) - TAB_ORDER.indexOf(b));
    return sorted;
  }, [elements, elementsLoading]);

  const navigationType = useNavigationType();
  /** Restore the last tab on browser back (`POP`), or pick the tab from location.state. */
  const [activeTab, setActiveTab] = React.useState<ReportTab>(() => {
    if (navigationType === 'POP') {
      const saved = sessionStorage.getItem('dt-tab:reports') as ReportTab | null;
      if (saved) return saved;
    }
    const s = location.state as {
      editReport?: { sessionType?: string };
      viewReport?: { sessionType?: string };
    } | null;
    const st = s?.editReport?.sessionType ?? s?.viewReport?.sessionType;
    if (st) {
      const tab = sessionTypeToReportTab(st);
      if (tab !== 'all') return tab as ReportTab;
    }
    return 'tutoring';
  });
  React.useEffect(() => {
    sessionStorage.setItem('dt-tab:reports', activeTab);
  }, [activeTab]);
  const [activeForm, setActiveForm] = React.useState<ActiveForm>(() => {
    const s = location.state as { editReport?: { reportId?: string } } | null;
    return s?.editReport?.reportId ? 'edit' : null;
  });
  const [editId, setEditId] = React.useState<string | null>(() => {
    const s = location.state as { editReport?: { reportId?: string } } | null;
    return s?.editReport?.reportId ?? null;
  });
  const [typeModalOpen, setTypeModalOpen] = React.useState(false);
  const [selectedReportType, setSelectedReportType] = React.useState<ReportTab | null>(null);

  const { viewMode, setViewMode: handleViewModeChange } = useViewMode('reports', 'list');
  const { openRowId, openRow, setOpenRow, closeRow } = useDetailsView();

  /** Close the form and drop edit / create-type state. */
  const closeForm = React.useCallback(() => {
    setActiveForm(null);
    setEditId(null);
    setSelectedReportType(null);
  }, []);

  /** Types creatable from `action-create-*` RBAC; falls back to allowed tabs. */
  const creatableTabIds = React.useMemo((): ReportTab[] => {
    const TAB_IDS = new Set<ReportTab>([
      'tutoring',
      'information-meeting',
      'welcome-meeting',
      'field-event',
    ]);
    if (creatableTypesRaw.length > 0) {
      return creatableTypesRaw.filter((id): id is ReportTab => TAB_IDS.has(id as ReportTab));
    }
    return (allowedTabIds ?? []).filter((id) => id !== 'all') as ReportTab[];
  }, [creatableTypesRaw, allowedTabIds]);

  /** Skip the type modal when only one report type is creatable. */
  const handleCreateClick = React.useCallback(() => {
    if (creatableTabIds.length === 1) {
      setSelectedReportType(creatableTabIds[0]);
      setActiveForm('create');
      setTypeModalOpen(false);
      closeRow();
    } else {
      setTypeModalOpen(true);
      setEditId(null);
      closeRow();
    }
  }, [creatableTabIds, closeRow]);

  /** After the type modal: open the create form for that report type. */
  const handleTypeSelect = (type: ReportTab) => {
    setSelectedReportType(type);
    setActiveForm('create');
    setTypeModalOpen(false);
    closeRow();
  };

  /** Open the edit form and switch tab when `sessionType` is known. */
  const handleEditReport = React.useCallback(
    (reportId: string, sessionType?: string) => {
      if (sessionType) {
        const tab = sessionTypeToReportTab(sessionType);
        if (tab !== 'all') setActiveTab(tab as ReportTab);
      }
      if (!reportId) return;
      setEditId(reportId);
      setActiveForm('edit');
      closeRow();
    },
    [closeRow],
  );

  /** Deep-link: filter the list by tutor / student passed via location.state. */
  const [personFilter, setPersonFilter] = React.useState<{
    tutorUserId?: string;
    studentUserId?: string;
    personName?: string;
  }>(() => {
    const state = location.state as {
      tutorUserId?: string;
      studentUserId?: string;
      personName?: string;
    } | null;
    return {
      tutorUserId: state?.tutorUserId,
      studentUserId: state?.studentUserId,
      personName: state?.personName,
    };
  });

  /** Deep-link: open edit/view or apply tutor/student filter from location.state, then clear it. */
  React.useEffect(() => {
    const state = location.state as {
      editReport?: { sessionId: string; reportId: string; sessionType?: string };
      viewReport?: { reportId: string; sessionType?: string };
      tutorUserId?: string;
      studentUserId?: string;
      personName?: string;
    } | null;
    if (state?.editReport) {
      handleEditReport(state.editReport.reportId, state.editReport.sessionType);
      void navigate(location.pathname, { replace: true, state: {} });
    } else if (state?.viewReport) {
      if (state.viewReport.sessionType) {
        const tab = sessionTypeToReportTab(state.viewReport.sessionType);
        if (tab !== 'all') setActiveTab(tab as ReportTab);
      }
      setOpenRow(state.viewReport.reportId);
      void navigate(location.pathname, { replace: true, state: {} });
    } else if (state?.tutorUserId || state?.studentUserId) {
      setPersonFilter({
        tutorUserId: state.tutorUserId,
        studentUserId: state.studentUserId,
        personName: state.personName,
      });
      void navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, navigate, handleEditReport, setOpenRow]);

  /** Close the form and refresh remote report queries. */
  const handleFormSuccess = React.useCallback(() => {
    closeForm();
    void queryClient.invalidateQueries({ queryKey: reportKeys.all });
  }, [closeForm, queryClient]);

  const formContent = React.useMemo(() => {
    if (activeForm === 'edit' && editId) {
      return (
        <EditReportView
          key={editId}
          reportId={editId}
          onClose={closeForm}
          onSuccess={handleFormSuccess}
        />
      );
    }
    if (activeForm === 'create' && selectedReportType) {
      return (
        <CreateReportView
          sessionType={selectedReportType}
          onClose={closeForm}
          onSuccess={handleFormSuccess}
        />
      );
    }
    return null;
  }, [activeForm, editId, selectedReportType, closeForm, handleFormSuccess]);

  /** Hide the tab bar when RBAC leaves only one report type (`all` does not count). */
  const nonToutAllowedTabs = React.useMemo(
    () => (allowedTabIds ?? []).filter((id) => id !== 'all') as ReportTab[],
    [allowedTabIds],
  );
  const showTabs = nonToutAllowedTabs.length > 1;

  const tabItems = React.useMemo(
    () =>
      (allowedTabIds ?? []).map((id) => {
        if (id === 'all') {
          return {
            id,
            label: TAB_LABELS[id],
            icon: <LayoutGrid className="h-4 w-4" />,
            color: TAB_COLORS[id],
          };
        }
        const sessionTypesForTab = TAB_SESSION_TYPES[id];
        const firstType = sessionTypesForTab?.[0];
        const Icon = firstType ? SESSION_TYPE_CONFIG[firstType]?.icon : undefined;

        return {
          id,
          label: TAB_LABELS[id],
          icon: Icon ? <Icon className="h-4 w-4" /> : undefined,
          color: TAB_COLORS[id],
        };
      }),
    [allowedTabIds],
  );

  return (
    <ManagementPage
      title="Gestion des Comptes Rendus"
      subtitle="Créez, modifiez et gérez tous vos comptes rendus de séances"
      tabs={
        showTabs
          ? {
              items: tabItems,
              activeTab,
              onTabChange: (id) => {
                closeForm();
                closeRow();
                setActiveTab(id as ReportTab);
              },
            }
          : undefined
      }
      actions={
        canCreate ? (
          <Button preset="create-entity" onClick={handleCreateClick}>
            Créer un compte rendu
          </Button>
        ) : undefined
      }
    >
      <CollapseReplaceWrapper
        mode="replace"
        isOpen={activeForm !== null}
        collapsePosition="top"
        formContent={formContent}
      >
        {/** Banner shown when the list is filtered to one tutor / student. */}
        {personFilter.personName && (
          <div className="mb-4 p-3 rounded-lg bg-[color-mix(in_srgb,var(--color-2-nuance-1)_10%,transparent)] border border-[var(--color-2-nuance-1)] flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium" style={{ color: 'var(--color-2-nuance-1)' }}>
                Comptes rendus de {personFilter.personName}
              </span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setPersonFilter({
                  tutorUserId: undefined,
                  studentUserId: undefined,
                  personName: undefined,
                });
                void navigate(location.pathname, { replace: true, state: {} });
              }}
            >
              Réinitialiser
            </Button>
          </div>
        )}

        <ReportsDataTable
          activeTab={activeTab}
          allowedTabIds={allowedTabIds}
          viewMode={viewMode}
          onViewModeChange={handleViewModeChange}
          expandedRowId={openRowId}
          onToggleRow={openRow}
          onCloseDetails={closeRow}
          onEditReport={handleEditReport}
          enableFilters={true}
          pageActions={{
            canEditRowTutoring,
            canEditRowInformation,
            canEditRowWelcome,
            canEditRowFieldEvent,
            canValidateTutoring,
            canValidateInformation,
            canValidateWelcome,
            canValidateFieldEvent,
          }}
          showExport={canExportExcel}
          initialFilters={{
            tutorUserId: personFilter.tutorUserId,
            studentUserId: personFilter.studentUserId,
          }}
        />
      </CollapseReplaceWrapper>

      <SelectReportTypeModal
        open={typeModalOpen}
        onClose={() => setTypeModalOpen(false)}
        onSelect={handleTypeSelect}
        allowedTabIds={creatableTabIds}
      />
    </ManagementPage>
  );
}
